import { pluralize } from 'inflection';
import { FieldInfo, TableInfo } from './schema-parser';
import { mapDrizzleTypeToTS, toKebabCase, toPascalCase, toSnakeCase } from './utils';

export function generateDTO(table: TableInfo): string {
  const className = toPascalCase(table.variableName);

  let dto = `// 请修改该文件代码以满足需求
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { IsDefined, IsNumber, IsOptional, IsString, MaxLength, IsInt, IsBoolean, IsUUID, IsDate, IsObject, IsArray } from 'class-validator';
import { Type } from 'class-transformer';
import { FileAttachment } from '../../../database/schema';

`;
  dto += `export class Create${className}Dto {\n`;

  for (const field of table.fields) {
    // 跳过主键
    if (
      field.isPrimaryKey || field.name === 'id' ||
      field.name.startsWith('_') ||
      field.name.startsWith('created') ||
      field.name.startsWith('updated')
    ) {
      continue;
    }

    // if (field.comment) {
    //   dto += `  /** ${field.comment} */\n`;
    // }

    const tsType = mapDrizzleTypeToTS(field);
    const optional = field.nullable || field.hasDefault ? '?' : '';

    // 添加验证装饰器注释
    const decorators = generateValidationDecorators(field);
    if (decorators) {
      dto += decorators;
    }

    dto += `  ${field.name}${optional}: ${tsType};\n\n`;
  }

  dto += '}\n\n';

  // Update DTO
  dto += `export class Update${className}Dto {\n`;

  for (const field of table.fields) {
    // 跳过系统字段和主键
    if (
      field.name.startsWith('_') ||
      field.name.startsWith('created') ||
      field.name.startsWith('updated') ||
      field.isPrimaryKey || field.name === 'id'
    ) {
      continue;
    }

    // 先不插入注释
    // if (field.comment) {
    //   dto += `  /** ${field.comment} */\n`;
    // }

    const tsType = mapDrizzleTypeToTS(field);

    const decorators = generateValidationDecorators(field, {
      isUpdate: true,
    });
    if (decorators) {
      dto += decorators;
    }

    dto += `  ${field.name}?: ${tsType};\n\n`;
  }

  dto += '}\n\n';

  // Response DTO
  dto += `export class ${className}ResponseDto {\n`;

  for (const field of table.fields) {
    // if (field.comment) {
    //   dto += `  /** ${field.comment} */\n`;
    // }

    const tsType = mapDrizzleTypeToTS(field);
    const optional = field.nullable ? '?' : '';

    const decorators = generateValidationDecorators(field, {
      isResponse: true,
    });
    if (decorators) {
      dto += decorators;
    }

    dto += `  ${field.name}${optional}: ${tsType};\n\n`;
  }

  dto += '}\n';

  return dto;
}

// 生成验证装饰器提示
export function generateValidationDecorators(field: FieldInfo, {
  isUpdate = false,
  isResponse = false,
}: { isUpdate?: boolean, isResponse?: boolean } = {}): string {
  let decorators = '  // 请按用户需求修改以下装饰器注释\n';

  if (field.nullable || (!isResponse && field.hasDefault) || isUpdate) {
    decorators += `  @ApiPropertyOptional({ description: '${field.comment || field.name}' })\n`;
    if (isResponse) {
      return decorators;
    }
    decorators += '  @IsOptional()\n';
  } else {
    decorators += `  @ApiProperty({ description: '${field.comment || field.name}' })\n`;
    if (isResponse) {
      return decorators;
    }
    decorators += '  @IsDefined()\n';
  }

  // 根据字段类型和约束生成装饰器注释
  switch (field.type) {
    case 'varchar':
    case 'char':
    case 'text':
      decorators += '  @IsString()\n';
      if (field.length) {
        decorators += `  @MaxLength(${field.length})\n`;
      }
      break;

    case 'integer':
    case 'smallint':
    case 'serial':
    case 'smallserial':
      decorators += '  @IsInt()\n';
      break;

    case 'decimal':
    case 'numeric':
    case 'real':
    case 'doublePrecision':
      decorators += '  @IsNumber()\n';
      break;

    case 'boolean':
      decorators += '  @IsBoolean()\n';
      break;

    case 'uuid':
      decorators += '  @IsUUID()\n';
      break;

    case 'timestamp':
    case 'timestamptz':
    case 'date':
    case 'customTimestamptz':
      decorators += '  @IsDate()\n';
      break;

    case 'json':
    case 'jsonb':
      decorators += '  @IsObject()\n';
      break;

    // case 'inet':
    // case 'cidr':
    //   decorators += '  // @IsIP()\n';
    //   break;
  }

  if (field.isArray) {
    decorators += '  @IsArray()\n';
  }

  // if (field.isUnique) {
  //   decorators += '  // @IsUnique() // Custom validator needed\n';
  // }

  // if (field.enumValues) {
  //   decorators += `  // @IsEnum([${field.enumValues.map((v) => `'${v}'`).join(', ')}])\n`;
  // }

  return decorators;
}

// Controller 生成器
export function generateController(table: TableInfo): string {
  const className = toPascalCase(table.variableName);
  const routePath = toKebabCase(pluralize(table.variableName));
  const filePath = toSnakeCase(table.variableName);

  // 找到主键字段
  const pkField = table.fields.find((f) => f.isPrimaryKey);
  const pkType = pkField ? mapDrizzleTypeToTS(pkField) : 'string';
  const pkName = pkField ? pkField.name : 'id';

  const controller = `
// 请修改该文件代码以满足需求
import { 
  Controller, 
  Get, 
  Post, 
  Put, 
  Delete, 
  Body, 
  Param, 
  Query,
} from '@nestjs/common';
 import {
  ApiTags,
  ApiOperation,
  ApiOkResponse,
  ApiCreatedResponse,
} from '@nestjs/swagger';
import { 
  Create${className}Dto, 
  Update${className}Dto,
  ${className}ResponseDto
} from './dtos/${filePath}.dto';
import { ${className}Service } from './${filePath}.service';

@ApiTags('${toPascalCase(table.variableName)}')
@Controller('api/${routePath}')
export class ${className}Controller {
  constructor(private readonly ${table.variableName}Service: ${className}Service) {}

  @Post()
  @ApiOperation({
    summary: '创建一条记录（模板内容，请修改我）',
    description: '创建一条记录（模板内容，请修改我）',
  })
  @ApiCreatedResponse({
    description: '成功创建一条记录',
    type: ${className}ResponseDto,
  })
  async create(
    @Body() createDto: Create${className}Dto
  ): Promise<${className}ResponseDto> {
    return this.${table.variableName}Service.create(createDto);
  }

  @ApiOperation({
    summary: '根据主键查询一条记录（模板内容，请修改我）',
    description: '根据主键查询一条记录（模板内容，请修改我）',
  })
  @ApiOkResponse({
    description: '成功查询一条记录',
    type: ${className}ResponseDto,
  })
  @Get(':${pkName}')
  async findOne(
    @Param('${pkName}') ${pkName}: ${pkType}
  ): Promise<${className}ResponseDto> {
    return this.${table.variableName}Service.findOne(${pkName});
  }

  @ApiOperation({
    summary: '根据主键更新一条记录（模板内容，请修改我）',
    description: '根据主键更新一条记录（模板内容，请修改我）',
  })
  @ApiOkResponse({
    description: '成功更新一条记录',
    type: ${className}ResponseDto,
  })
  @Put(':${pkName}')
  async update(
    @Param('${pkName}') ${pkName}: ${pkType},
    @Body() updateDto: Update${className}Dto
  ): Promise<${className}ResponseDto> {
    return this.${table.variableName}Service.update(${pkName}, updateDto);
  }

  @ApiOperation({
    summary: '根据主键删除一条记录（模板内容，请修改我）',
    description: '根据主键删除一条记录（模板内容，请修改我）',
  })
  @ApiOkResponse({
    description: '成功删除一条记录',
  })
  @Delete(':${pkName}')
  async remove(
    @Param('${pkName}') ${pkName}: ${pkType}
  ): Promise<void> {
    return this.${table.variableName}Service.remove(${pkName});
  }
}
`;

  return controller;
}

// Service 生成器
export function generateService(table: TableInfo): string {
  const className = toPascalCase(table.variableName);
  const filePath = toSnakeCase(table.variableName);

  const pkField = table.fields.find((f) => f.isPrimaryKey);
  const pkType = pkField ? mapDrizzleTypeToTS(pkField) : 'string';
  const pkName = pkField ? pkField.name : 'id';

  const service = `
// 请修改该文件代码以满足需求
import { Injectable, Inject, Logger, NotFoundException } from '@nestjs/common';
import { eq } from 'drizzle-orm';
import { DRIZZLE_DATABASE, type PostgresJsDatabase } from '@lark-apaas/fullstack-nestjs-core';
import { ${table.variableName} } from '../../database/schema';
import { 
  Create${className}Dto, 
  Update${className}Dto,
  ${className}ResponseDto 
} from './dtos/${filePath}.dto';

@Injectable()
export class ${className}Service {
  private readonly logger = new Logger(${className}Service.name);

  constructor(@Inject(DRIZZLE_DATABASE) private readonly db: PostgresJsDatabase) {}

  async create(createDto: Create${className}Dto): Promise<${className}ResponseDto> {
    const [result] = await this.db
      .insert(${table.variableName})
      .values(createDto)
      .returning();

    this.logger.log(\`Created ${className} with ${pkName} \${result.${pkName}}\`);
    
    return result;
  }

  async findAll(options?: { page?: number; limit?: number }): Promise<${className}ResponseDto[]> {
    const { page = 1, limit = 10 } = options || {};
    const offset = (page - 1) * limit;

    return this.db
      .select()
      .from(${table.variableName})
      .limit(limit)
      .offset(offset);
  }

  async findOne(${pkName}: ${pkType}): Promise<${className}ResponseDto> {
    const [result] = await this.db
      .select()
      .from(${table.variableName})
      .where(eq(${table.variableName}.${pkName}, ${pkName}))
      .limit(1);

    if (!result) {
      throw new NotFoundException(\`${className} with ${pkName} \${${pkName}} not found\`);
    }

    return result;
  }

  async update(${pkName}: ${pkType}, updateDto: Update${className}Dto): Promise<${className}ResponseDto> {
    const [result] = await this.db
      .update(${table.variableName})
      .set(updateDto)
      .where(eq(${table.variableName}.${pkName}, ${pkName}))
      .returning();

    if (!result) {
      throw new NotFoundException(\`${className} with ${pkName} \${${pkName}} not found\`);
    }

    return result;
  }

  async remove(${pkName}: ${pkType}): Promise<void> {
    const result = await this.db
      .delete(${table.variableName})
      .where(eq(${table.variableName}.${pkName}, ${pkName}))
      .returning();

    if (result.length === 0) {
      throw new NotFoundException(\`${className} with ${pkName} \${${pkName}} not found\`);
    }

    this.logger.log(\`Deleted ${className} with ${pkName} \${${pkName}}\`);
  }
}
`;

  return service;
}

export function generateModule(table: TableInfo): string {
  const className = toPascalCase(table.variableName);
  const filePath = toSnakeCase(table.variableName);

  const module = `
import { Module } from '@nestjs/common';
import { ${className}Controller } from './${filePath}.controller';
import { ${className}Service } from './${filePath}.service';

@Module({
  controllers: [${className}Controller],
  providers: [${className}Service],
})
export class ${className}Module {}
`;

  return module;
}
